import { InputChangeEventDetail } from "@ionic/core";
import { IonInput } from "@ionic/react";
import { ComponentProps, ReactNode, useEffect, useMemo, useRef } from "react";
import { Control, FieldError, FieldPath, FieldValues, Merge, useController } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import Error from "@components/fields/Error";
import styles from "@components/fields/Input.module.css";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import Icon from "@components/text/Icon";
import TinyText from "@components/text/TinyText";
import InputLabel from "@components/typography/InputLabel";
import useAutofocus from "@hooks/useAutofocus";
import { IconName } from "@utils/iconUtils";

type Props<FormData extends FieldValues> = Omit<ComponentProps<typeof IonInput>, "size"> & {
  contentUnder?: boolean;
  control: Control<FormData>;
  error: Merge<FieldError, (FieldError | undefined)[]> | undefined;
  extraRightSpacing?: boolean;
  hoverOutline?: boolean;
  icon?: IconName;
  inputStyles?: boolean;
  instructions?: string | null;
  isOption?: boolean;
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  noSpacing?: boolean;
  onWhatsThis?: () => void;
  required?: boolean;
  size?: "normal" | "large";
};

const Input = <FormData extends FieldValues>({
  autofocus,
  contentUnder = false,
  control,
  error,
  extraRightSpacing,
  hoverOutline = false,
  icon,
  inputStyles = false,
  instructions,
  isOption = false,
  label,
  name,
  noSpacing,
  onWhatsThis,
  required,
  size = "normal",
  ...inputProps
}: Props<FormData>) => {
  const className = `${styles.input} ${styles[size]} ${error ? styles.error : ""} ${
    extraRightSpacing ? styles.extraRightSpacing : ""
  } ${inputProps.type === "date" && !inputStyles ? styles.date : ""}`;

  const inputRef = useRef<HTMLIonInputElement>(null);

  useAutofocus(!!autofocus, inputRef);

  const arrayErrors = useMemo(() => {
    if (error === undefined) {
      return undefined;
    }

    if (!Array.isArray(error)) {
      return [error];
    }
    return error;
  }, [error]);

  const {
    field: { onBlur, onChange, value }
  } = useController({ control, name });

  useEffect(() => {
    if (inputProps.type === "number") {
      const current = inputRef.current;

      current?.addEventListener("wheel", e => e.preventDefault());
      return () => {
        current?.removeEventListener("wheel", e => e.preventDefault());
      };
    }
  }, [inputProps.type, inputRef]);

  return (
    <>
      <div
        className={`${isOption ? styles.optionWrapperItem : styles.wrapperItem} ${styles[size]} ${
          noSpacing ? styles.noMargin : ""
        } ${hoverOutline ? styles.hoverOutline : ""}`}
      >
        <div className={styles.labelRow}>
          <div>
            {label && (
              <div>
                <InputLabel id={name}>
                  {label}
                  {required && <RequiredFieldIndicator />}
                </InputLabel>
              </div>
            )}
            {icon && (
              <div className={styles.iconWrapper}>
                <Icon color="medium" icon={icon} />
              </div>
            )}
          </div>
          {onWhatsThis && (
            <div className={styles.whatsThis} onClick={onWhatsThis}>
              <Icon icon="infoCircle" />
              <InputLabel>
                <FormattedMessage id="forms.whatsThis" />
              </InputLabel>
            </div>
          )}
        </div>

        <IonInput
          aria-describedby={instructions || error ? `${name}-instructions-or-error` : ""}
          aria-invalid={error?.message?.length ? "true" : "false"}
          aria-label={name}
          aria-required={required}
          autocomplete="off"
          className={`${value ? className + " " + styles.valueEntered : className} ${icon ? styles.withIcon : ""} ${
            contentUnder ? styles.contentUnder : ""
          }`}
          defaultValue={123}
          name={name}
          onIonBlur={onBlur}
          onIonChange={(event: CustomEvent<InputChangeEventDetail>) => {
            if (event.detail.value !== value) {
              onChange(event.detail.value);
            }
          }}
          ref={inputRef}
          value={value}
          {...inputProps}
        />
      </div>
      {(instructions || (arrayErrors && arrayErrors.length > 0)) && (
        <div id={`${name}-instructions-or-error`}>
          {instructions && <TinyText>{instructions}</TinyText>}
          {arrayErrors?.map(error => <Error error={error} key={error?.message} />)}
        </div>
      )}
    </>
  );
};

export default Input;
